import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Package,
  Building2,
  Calendar,
  User,
  Paperclip,
  Send,
  Upload,
  History,
  Headphones,
  Receipt,
  ShieldCheck,
  Gavel,
  MessageSquare,
  X,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import moment from "moment";
import { Drawer } from "../../../components/ui/Drawer";
import { Badge } from "../../../components/ui/Badge";
import { CopyButton } from "../../../components/ui/CopyButton";
import { Avatar } from "../../../components/ui/Avatar";
import { Button } from "../../../components/ui/Button";
import { Select } from "../../../components/ui/Select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/Tabs";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import { Skeleton } from "../../../components/ui/Skeleton";
import { RelativeTime } from "../../../components/ui/RelativeTime";
import { getInitials } from "../../../utils/getInitials";
import { getPackageIncident } from "../../../api/packages";
import { showToast } from "../../../utils/showToast";
import SacTab from "../../../components/incidentDetail/SacTab";
import AdministrationTab from "../../../components/incidentDetail/AdministrationTab";
import SupervisionTab from "../../../components/incidentDetail/SupervisionTab";
import AppealTab from "../../../components/incidentDetail/AppealTab";
import "../../../components/incidentDetail/IncidentDetail.scss";
import {
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  INCIDENT_RESPONSIBLE,
  INCIDENT_TYPES_BY_CATEGORY,
  INCIDENT_CATEGORIES,
  DEPARTMENT_LABELS,
} from "../../../constants/incidents";
import {
  SEVERITY_TONE,
  COLUMN_TONE,
  typeLabel,
  clientName,
  followingNumber,
  assignee,
  departmentProgress,
  rawStatus,
  columnFor,
} from "./ticketModel";

// Construye una bitácora unificada (historial) a partir de los comentarios
// reales de todos los departamentos + la creación. Es lo más cercano a un
// historial que ofrece el back hoy.
function buildTimeline(incident, t) {
  if (!incident) return [];
  const events = [];
  if (incident.createdAt) {
    const by = incident.createdBy || incident.createdByUser;
    events.push({
      at: incident.createdAt,
      kind: "creada",
      title: t("Ticket creado"),
      author: by ? `${by.name || ""} ${by.lastName || ""}`.trim() : null,
    });
  }
  const deptComments = (bucket, deptKey) =>
    (Array.isArray(incident[bucket]?.comments) ? incident[bucket].comments : []).forEach((c) =>
      events.push({
        at: c.createdAt,
        kind: "comentario",
        dept: deptKey,
        title: c.comment,
        author: c.createdBy ? `${c.createdBy.name || ""} ${c.createdBy.lastName || ""}`.trim() : null,
      }),
    );
  // comentarios sueltos legacy
  (Array.isArray(incident.comments) ? incident.comments : []).forEach((c) =>
    events.push({
      at: c.createdAt,
      kind: "comentario",
      title: c.comment,
      author: c.createdBy ? `${c.createdBy.name || ""} ${c.createdBy.lastName || ""}`.trim() : null,
    }),
  );
  deptComments("sac", "sac");
  deptComments("administration", "administration");
  deptComments("supervision", "supervision");
  ["sac", "administration", "supervision"].forEach((d) => {
    if (incident[d]?.resolvedAt) {
      events.push({
        at: incident[d].resolvedAt,
        kind: "resuelto",
        dept: d,
        title: t("{{dept}} marcado como resuelto", { dept: t(DEPARTMENT_LABELS[d] || d) }),
      });
    }
  });
  return events
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at) - new Date(a.at));
}

export default function TicketDetailDrawer({ incidentId, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("sac");

  // Estado de front (sin back) para asignación/tipificación/adjuntos/nota.
  const [draftResponsible, setDraftResponsible] = useState("");
  const [draftDepartment, setDraftDepartment] = useState("");
  const [draftType, setDraftType] = useState("");
  const [localAttachments, setLocalAttachments] = useState([]);
  const fileRef = useRef(null);

  // `silent`: recarga sin volver al skeleton. Si no, el refresco que pide
  // AppealTab tras resolver lo desmonta y se pierde su aviso en línea.
  const load = useCallback(
    async (id, { silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const res = await getPackageIncident(id);
        if (res.success && res.data) {
          setIncident(res.data);
          setDraftDepartment(
            (res.data.requiredDepartments && res.data.requiredDepartments[0]) || "sac",
          );
          setDraftType(res.data.incidentType || "");
        } else {
          showToast(t("Error al cargar la incidencia"), false);
        }
      } catch {
        showToast(t("Error al cargar la incidencia"), false);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (incidentId) load(incidentId);
  }, [incidentId, load]);

  const handleTabUpdated = (updated) => {
    if (updated) setIncident(updated);
    else load(incidentId, { silent: true });
    onUpdated?.();
  };

  const status = incident ? rawStatus(incident) : "abierta";
  const type = incident ? typeLabel(incident) : null;
  const client = incident ? clientName(incident) : null;
  const follow = incident ? followingNumber(incident) : null;
  const person = incident ? assignee(incident) : null;
  const progress = incident ? departmentProgress(incident) : { done: 0, total: 0, required: [] };
  const requiredDepts = incident?.requiredDepartments || ["sac"];

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setLocalAttachments((prev) => [
      ...prev,
      ...files.map((f) => ({ name: f.name, size: f.size })),
    ]);
    e.target.value = "";
  };

  const currentColumn = incident ? columnFor(incident) : "nuevo";
  const header = incident ? (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge tone={COLUMN_TONE[currentColumn] || "neutral"} dot>
        {t(INCIDENT_STATUS[status] || status)}
      </Badge>
      {incident.severity && (
        <Badge tone={SEVERITY_TONE[incident.severity] || "neutral"} dot>
          {t(INCIDENT_SEVERITY[incident.severity] || incident.severity)}
        </Badge>
      )}
    </div>
  ) : null;

  return (
    <Drawer
      open={!!incidentId}
      onClose={onClose}
      size="lg"
      title={t("Ticket de incidencia")}
      description={follow ? `${t("Pedido")} ${follow}` : t("Sin pedido asociado")}
      headerActions={header}
    >
      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : !incident ? (
        <p className="text-sm text-fg-muted">{t("No se pudo cargar la incidencia.")}</p>
      ) : (
        <div className="space-y-5">
          {/* ── Título / tipificación ── */}
          <div>
            <h3 className="text-lg font-semibold text-fg leading-snug">
              {t(type || "Incidencia sin tipificar")}
            </h3>
            {incident.description && (
              <p className="text-sm text-fg-muted mt-1 whitespace-pre-line">
                {incident.description}
              </p>
            )}
          </div>

          {/* ── Ficha rápida ── */}
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                icon: Building2,
                label: t("Empresa"),
                value: client || "—",
              },
              {
                icon: Package,
                label: t("N° seguimiento"),
                value: follow || "—",
                copy: follow,
              },
              {
                icon: Calendar,
                label: t("Creada"),
                value: moment(incident.createdAt).format("DD/MM/YYYY HH:mm"),
              },
              {
                icon: User,
                label: t("Creado por"),
                value: (() => {
                  const by = incident.createdBy || incident.createdByUser;
                  return by ? `${by.name || ""} ${by.lastName || ""}`.trim() || "—" : "—";
                })(),
              },
            ].map(({ icon: Icon, label, value, copy }) => (
              <div
                key={label}
                className="flex items-start gap-2 rounded-lg border border-border bg-bg-muted/50 p-2.5"
              >
                <span className="w-7 h-7 rounded-md bg-bg flex items-center justify-center text-fg-muted shrink-0">
                  <Icon className="w-3.5 h-3.5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-fg-muted">
                    {label}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <div className="text-sm text-fg truncate">{value}</div>
                    <CopyButton
                      value={copy}
                      label={t("Copiar número de seguimiento")}
                      copiedMessage={t("Número de seguimiento copiado")}
                      className="shrink-0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Asignación + tipificación (FRONT-FIRST) ── */}
          <section className="rounded-lg border border-border bg-bg-muted/50 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-fg-muted" aria-hidden />
              <h4 className="text-sm font-semibold text-fg">{t("Asignación y tipificación")}</h4>
            </div>

            {/* Responsable actual (real, solo lectura desde el back) */}
            <div className="flex items-center gap-2.5 rounded-lg bg-bg p-2.5 border border-border">
              {person ? (
                <>
                  <Avatar size="sm" initials={getInitials(person.name, person.lastName)} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-fg">
                      {`${person.name || ""} ${person.lastName || ""}`.trim()}
                    </div>
                    <div className="text-xs text-fg-muted">{t("Responsable actual")}</div>
                  </div>
                </>
              ) : (
                <span className="text-sm text-fg-subtle italic">{t("Sin responsable asignado")}</span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                label={t("Reasignar responsable")}
                value={draftResponsible}
                onChange={(e) => setDraftResponsible(e.target.value)}
                placeholder={t("Selecciona un perfil")}
                options={Object.entries(INCIDENT_RESPONSIBLE).map(([k, v]) => ({
                  value: k,
                  label: t(v),
                }))}
              />
              <Select
                label={t("Área responsable")}
                value={draftDepartment}
                onChange={(e) => setDraftDepartment(e.target.value)}
                options={Object.entries(DEPARTMENT_LABELS).map(([k, v]) => ({
                  value: k,
                  label: t(v),
                }))}
              />
            </div>

            <Select
              label={t("Tipificación")}
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
            >
              <option value="">{t("Sin tipificar")}</option>
              {Object.entries(INCIDENT_TYPES_BY_CATEGORY).map(([catKey, types]) => (
                <optgroup key={catKey} label={t(INCIDENT_CATEGORIES[catKey] || catKey)}>
                  {Object.entries(types).map(([tk, tl]) => (
                    <option key={tk} value={tk}>
                      {t(tl)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>

            <PendingBackendNotice>
              {t("Reasignar responsable, área y tipificación está en construcción: por ahora el cambio queda solo en pantalla.")}
            </PendingBackendNotice>
          </section>

          {/* ── Adjuntos (FRONT-FIRST) ── */}
          <section className="rounded-lg border border-border bg-bg-muted/50 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-fg-muted" aria-hidden />
                <h4 className="text-sm font-semibold text-fg">{t("Adjuntos")}</h4>
              </div>
              <Button
                size="sm"
                variant="outline"
                iconLeft={<Upload className="w-4 h-4" />}
                onClick={() => fileRef.current?.click()}
              >
                {t("Subir archivo")}
              </Button>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={onPickFiles}
              />
            </div>
            {localAttachments.length === 0 ? (
              <p className="text-sm text-fg-subtle italic">{t("Aún no hay adjuntos.")}</p>
            ) : (
              <ul className="space-y-1.5">
                {localAttachments.map((a, i) => (
                  <li
                    key={`${a.name}-${i}`}
                    className="flex items-center justify-between gap-2 rounded-lg bg-bg border border-border px-3 py-2 text-sm"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <Paperclip className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden />
                      <span className="truncate text-fg">{a.name}</span>
                    </span>
                    <button
                      type="button"
                      className="text-fg-muted hover:text-status-error"
                      onClick={() =>
                        setLocalAttachments((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      aria-label={t("Quitar adjunto")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <PendingBackendNotice>
              {t("La subida de adjuntos está en construcción: por ahora quedan solo en esta sesión.")}
            </PendingBackendNotice>
          </section>

          {/* ── Gestión: apelación (panel dedicado) o departamentos (SAC / Admin / Supervisión) ── */}
          {incident.appeal ? (
            <section className="rounded-lg border border-border bg-bg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Gavel className="w-4 h-4 text-fg-muted" aria-hidden />
                <h4 className="text-sm font-semibold text-fg">{t("Revisión de la apelación")}</h4>
              </div>
              <div className="incident-detail-tabs-wrap">
                <AppealTab
                  incident={incident}
                  onUpdated={handleTabUpdated}
                  readOnly={["approved", "rejected"].includes(incident.appeal.status)}
                />
              </div>
            </section>
          ) : (
            <section className="rounded-lg border border-border bg-bg p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-fg-muted" aria-hidden />
                  <h4 className="text-sm font-semibold text-fg">{t("Resolución por área")}</h4>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
                  {progress.done >= progress.total && progress.total > 0 ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-status-success" aria-hidden />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 text-status-warning" aria-hidden />
                  )}
                  {t("{{done}} de {{total}} áreas resueltas", {
                    done: progress.done,
                    total: progress.total,
                  })}
                </span>
              </div>

              <div className="incident-detail-tabs-wrap">
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList ariaLabel={t("Áreas de resolución")}>
                    <TabsTrigger value="sac" icon={<Headphones />}>
                      {t("SAC")}
                      {requiredDepts.includes("sac") && (
                        <DeptDot resolved={incident.sac?.resolved || !!incident.resolvedAt} />
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="administration" icon={<Receipt />}>
                      {t("Administración")}
                      {requiredDepts.includes("administration") && (
                        <DeptDot resolved={incident.administration?.resolved} />
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="supervision" icon={<ShieldCheck />}>
                      {t("Supervisión")}
                      {requiredDepts.includes("supervision") && (
                        <DeptDot resolved={incident.supervision?.resolved} />
                      )}
                    </TabsTrigger>
                  </TabsList>

                  <div className="pt-4">
                    <TabsContent value="sac">
                      <SacTab
                        incident={incident}
                        onUpdated={handleTabUpdated}
                        readOnly={status === "cerrada" && !!incident.sac?.resolved}
                      />
                    </TabsContent>
                    <TabsContent value="administration">
                      <AdministrationTab
                        incident={incident}
                        onUpdated={handleTabUpdated}
                        readOnly={incident.administration?.resolved === true}
                      />
                    </TabsContent>
                    <TabsContent value="supervision">
                      <SupervisionTab
                        incident={incident}
                        onUpdated={handleTabUpdated}
                        readOnly={incident.supervision?.resolved === true}
                      />
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </section>
          )}

          {/* ── Historial / bitácora unificada (REAL, derivado) ── */}
          <TicketTimeline incident={incident} />

          {/* ── Nota interna rápida (FRONT-FIRST, comentario hilado) ── */}
          <QuickInternalNote />
        </div>
      )}
    </Drawer>
  );
}

function DeptDot({ resolved }) {
  return (
    <span
      className={[
        "ml-1 inline-block w-1.5 h-1.5 rounded-full",
        resolved ? "bg-status-success" : "bg-status-warning",
      ].join(" ")}
      aria-hidden
    />
  );
}

function TicketTimeline({ incident }) {
  const { t } = useTranslation();
  const events = buildTimeline(incident, t);
  return (
    <section className="rounded-lg border border-border bg-bg-muted/50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-fg-muted" aria-hidden />
        <h4 className="text-sm font-semibold text-fg">{t("Historial")}</h4>
      </div>
      {events.length === 0 ? (
        <p className="text-sm text-fg-subtle italic">{t("Sin movimientos registrados.")}</p>
      ) : (
        <ol className="relative pl-4 space-y-3 before:absolute before:left-1 before:top-1 before:bottom-1 before:w-px before:bg-border">
          {events.map((e, i) => (
            <li key={i} className="relative">
              <span
                className={[
                  "absolute -left-[11px] top-1 w-2 h-2 rounded-full ring-2 ring-bg-muted",
                  e.kind === "resuelto"
                    ? "bg-status-success"
                    : e.kind === "creada"
                    ? "bg-brand-500"
                    : "bg-status-info",
                ].join(" ")}
                aria-hidden
              />
              <div className="text-sm text-fg leading-snug">{e.title}</div>
              <div className="text-[11px] text-fg-muted mt-0.5">
                {e.author ? `${e.author} · ` : ""}
                <RelativeTime value={e.at} t={t} />
                {e.dept ? ` · ${t(DEPARTMENT_LABELS[e.dept] || e.dept)}` : ""}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// Nota interna: comentario hilado del front. El back hoy solo acepta
// comentarios por departamento (SAC/Admin/Supervisión); un hilo de notas
// internas transversal no existe todavía.
function QuickInternalNote() {
  const { t } = useTranslation();
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState("");

  const add = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setThread((prev) => [
      { body: draft.trim(), at: new Date().toISOString() },
      ...prev,
    ]);
    setDraft("");
  };

  return (
    <section className="rounded-lg border border-border bg-bg-muted/50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-fg-muted" aria-hidden />
        <h4 className="text-sm font-semibold text-fg">{t("Notas internas")}</h4>
      </div>
      <form onSubmit={add} className="space-y-2">
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("Escribe una nota interna…")}
          className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <div className="flex justify-end">
          <Button size="sm" type="submit" iconLeft={<Send className="w-4 h-4" />} disabled={!draft.trim()}>
            {t("Agregar nota")}
          </Button>
        </div>
      </form>
      {thread.length > 0 && (
        <ul className="space-y-2">
          {thread.map((n, i) => (
            <li key={i} className="rounded-lg bg-bg border border-border px-3 py-2">
              <p className="text-sm text-fg whitespace-pre-line">{n.body}</p>
              <RelativeTime value={n.at} t={t} className="text-[11px] text-fg-muted" />

            </li>
          ))}
        </ul>
      )}
      <PendingBackendNotice>
        {t("Las notas internas están en construcción: por ahora quedan solo en esta sesión.")}
      </PendingBackendNotice>
    </section>
  );
}
